<template>
  <div class="users-view">
    <div class="header">
      <h2>Users</h2>
    </div>

    <div v-if="loading" class="loading">
      Loading users...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else-if="users && users.length === 0" class="no-users">
      No users found.
    </div>
    <div v-else-if="users && users.length">
      <div class="upcoming-payments">
        <h3>Upcoming Payments (Next 30 Days)</h3>
        <div class="payments-summary">
          <div v-for="(total, currency) in upcomingPayments" :key="currency" class="payment-item">
            <span class="currency-label">{{ currency }}:</span>
            <span class="currency-amount">{{ formatCurrencyAmount(currency, total) }}</span>
          </div>
          <div v-if="Object.keys(upcomingPayments).length === 0" class="no-payments">
            No upcoming payments in the next 30 days
          </div>
        </div>
      </div>
      <div class="users-list">
      <div class="users-header">
        <div class="user-item">Name</div>
        <div class="user-item">Country</div>
        <div class="user-item">Subscription</div>
        <div class="user-item">Price</div>
        <div class="user-item">Next Billing Date</div>
        <div class="user-item">Email Verified</div>
        <div class="user-item">Phone Verified</div>
      </div>
      <div v-for="user in users" :key="user.id" class="user-row">
        <div class="user-item">
          <div class="user-name">
            {{ getUserName(user) }}
          </div>
        </div>
        <div class="user-item">
          {{ getCountry(user) }}
        </div>
        <div class="user-item">
          {{ getSubscriptionName(user) }}
        </div>
        <div class="user-item">
          {{ getPrice(user) }}
        </div>
        <div class="user-item">
          <div v-if="getNextBillingDate(user)" class="date">
            {{ formatDate(getNextBillingDate(user)) }}
          </div>
          <div v-else class="na-status">N/A</div>
        </div>
        <div class="user-item">
          <img v-if="user.isEmailVerified" src="../../assets/checkmark.png" alt="Verified" class="verified-check">
          <span v-else class="na-status">—</span>
        </div>
        <div class="user-item">
          <img v-if="user.isPhoneVerified" src="../../assets/checkmark.png" alt="Verified" class="verified-check">
          <span v-else class="na-status">—</span>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { api } from '../../utils/api'
import { useRouter } from 'vue-router'

export default {
  name: 'AdminUsersView',
  setup() {
    const router = useRouter()
    const users = ref([])
    const loading = ref(true)
    const error = ref('')

    const fetchUsers = async () => {
      try {
        loading.value = true
        error.value = ''
        const data = await api('/admin/users?withDomains=false', {}, router)
        users.value = data?.users || []
      } catch (err) {
        console.error('Failed to fetch users:', err)
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const getUserName = (user) => {
      const firstName = user.firstName || ''
      const lastName = user.lastName || ''
      const name = `${firstName} ${lastName}`.trim()
      return name || user.email || 'N/A'
    }

    const getCountry = (user) => {
      return user.billingProfile?.country || 'N/A'
    }

    const getSubscriptionName = (user) => {
      return user.subscription?.name || 'N/A'
    }

    const getPrice = (user) => {
      const subscription = user.subscription
      if (!subscription) {
        return 'N/A'
      }
      const basePrice = subscription.basePrice
      const currency = subscription.currency || ''
      
      if (basePrice === null || basePrice === undefined) {
        return currency || 'N/A'
      }
      
      // Format price with currency
      const formattedPrice = typeof basePrice === 'number' 
        ? basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : basePrice
      
      return currency ? `${currency} ${formattedPrice}` : formattedPrice
    }

    const getNextBillingDate = (user) => {
      return user.subscription?.nextBillingDate || null
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const day = date.getDate().toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${month} ${day}, ${year}`
    }

    const upcomingPayments = computed(() => {
      const now = new Date()
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
      const totals = {}

      users.value.forEach(user => {
        const subscription = user.subscription
        if (!subscription || !subscription.nextBillingDate) {
          return
        }

        const billingDate = new Date(subscription.nextBillingDate)
        
        // Check if billing date is within the next 30 days
        if (billingDate >= now && billingDate <= thirtyDaysFromNow) {
          const currency = subscription.currency
          const basePrice = subscription.basePrice

          if (currency && (basePrice !== null && basePrice !== undefined)) {
            if (!totals[currency]) {
              totals[currency] = 0
            }
            totals[currency] += typeof basePrice === 'number' ? basePrice : parseFloat(basePrice) || 0
          }
        }
      })

      return totals
    })

    const formatCurrencyAmount = (currency, amount) => {
      const formattedAmount = typeof amount === 'number' 
        ? amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : amount
      return `${currency} ${formattedAmount}`
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      users,
      loading,
      error,
      getUserName,
      getCountry,
      getSubscriptionName,
      getPrice,
      getNextBillingDate,
      formatDate,
      upcomingPayments,
      formatCurrencyAmount
    }
  }
}
</script>

<style scoped>
.users-view {
  padding: 20px 0;
  margin: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.loading, .error, .no-users {
  text-align: center;
  padding: 20px;
  font-size: 1.2em;
  color: var(--text-primary);
}

.error {
  color: var(--error-color);
}

.upcoming-payments {
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  margin-bottom: 20px;
  border: 1px solid var(--border-color);
  padding: 20px;
}

.upcoming-payments h3 {
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.payments-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
}

.payment-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency-label {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
}

.currency-amount {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
}

.no-payments {
  color: var(--text-muted);
  font-style: italic;
  font-size: 14px;
}

.users-list {
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  margin-top: 20px;
  border: 1px solid var(--border-color);
}

.users-header {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1.5fr 2fr 1fr 1fr;
  padding: 15px 20px;
  background: var(--bg-tertiary);
  border-bottom: 2px solid var(--border-color);
  font-weight: bold;
  color: var(--text-primary);
  font-size: 14px;
}

.user-row {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1.5fr 2fr 1fr 1fr;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  color: var(--text-primary);
  transition: background-color 0.2s ease;
  font-size: 14px;
}

.user-row:last-child {
  border-bottom: none;
}

.user-row:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.user-item {
  padding: 0 10px;
  display: flex;
  align-items: center;
}

.user-name {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

.na-status {
  color: var(--text-muted);
  font-style: italic;
}

.date {
  font-weight: 500;
  color: var(--text-primary);
}

.verified-check {
  width: 24px;
  height: 24px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .users-view {
    padding: 20px 0;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .users-header {
    grid-template-columns: 1fr 1fr;
    font-size: 12px;
    padding: 12px 15px;
  }

  .user-row {
    grid-template-columns: 1fr 1fr;
    padding: 12px 15px;
  }

  .user-name {
    font-size: 13px;
  }
}
</style>
